// ─────────────────────────────────────────────────────────────────────────────
// MOBILE CONTROLS  –  joystick (left) + jump / action (right)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useEffect, useCallback } from 'react';
import { setTouchAxis, setTouchJump, setTouchAction } from './engine/input';
import './MobileControls.css';

const STICK_RADIUS = 44;
const DEAD_ZONE    = 0.18;

export const MobileControls: React.FC = () => {
  const stickAreaRef = useRef<HTMLDivElement>(null);
  const knobRef      = useRef<HTMLDivElement>(null);
  const jumpBtnRef   = useRef<HTMLButtonElement>(null);
  const actionBtnRef = useRef<HTMLButtonElement>(null);

  const stickTouchId = useRef<number | null>(null);
  const stickOrigin  = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const jumpTouchId  = useRef<number | null>(null);
  const actionTouchId = useRef<number | null>(null);

  const onStickStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const touch          = e.changedTouches[0];
    stickTouchId.current = touch.identifier;
    const rect           = stickAreaRef.current!.getBoundingClientRect();
    stickOrigin.current  = {
      x: rect.left + rect.width  / 2,
      y: rect.top  + rect.height / 2,
    };
  }, []);

  const onStickMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier !== stickTouchId.current) continue;
      const dx    = t.clientX - stickOrigin.current.x;
      const dy    = t.clientY - stickOrigin.current.y;
      const dist  = Math.sqrt(dx * dx + dy * dy);
      const clamp = Math.min(dist, STICK_RADIUS);
      const ratio = clamp / STICK_RADIUS;
      const ax    = ratio < DEAD_ZONE ? 0 : (dx / Math.max(dist, 1)) * ratio;

      if (knobRef.current) {
        const kx = (dx / Math.max(dist, 1)) * clamp;
        const ky = (dy / Math.max(dist, 1)) * clamp;
        knobRef.current.style.transform = `translate(${kx}px,${ky}px)`;
      }
      setTouchAxis(ax);
    }
  }, []);

  const onStickEnd = useCallback((e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === stickTouchId.current) {
        stickTouchId.current = null;
        if (knobRef.current) knobRef.current.style.transform = 'translate(0,0)';
        setTouchAxis(0);
      }
    }
  }, []);

  const onJumpStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    jumpTouchId.current = e.changedTouches[0].identifier;
    setTouchJump(true);
  }, []);

  const onJumpEnd = useCallback((e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === jumpTouchId.current) {
        jumpTouchId.current = null;
        setTouchJump(false);
      }
    }
  }, []);

  const onActionStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    actionTouchId.current = e.changedTouches[0].identifier;
    setTouchAction(true);
  }, []);

  const onActionEnd = useCallback((e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === actionTouchId.current) {
        actionTouchId.current = null;
        setTouchAction(false);
      }
    }
  }, []);

  useEffect(() => {
    const stick  = stickAreaRef.current!;
    const jump   = jumpBtnRef.current!;
    const action = actionBtnRef.current!;

    stick.addEventListener('touchstart',  onStickStart, { passive: false });
    stick.addEventListener('touchmove',   onStickMove,  { passive: false });
    stick.addEventListener('touchend',    onStickEnd,   { passive: false });
    stick.addEventListener('touchcancel', onStickEnd,   { passive: false });

    jump.addEventListener('touchstart',   onJumpStart,  { passive: false });
    jump.addEventListener('touchend',     onJumpEnd,    { passive: false });
    jump.addEventListener('touchcancel',  onJumpEnd,    { passive: false });

    action.addEventListener('touchstart',   onActionStart, { passive: false });
    action.addEventListener('touchend',     onActionEnd,   { passive: false });
    action.addEventListener('touchcancel',  onActionEnd,   { passive: false });

    return () => {
      stick.removeEventListener('touchstart',  onStickStart);
      stick.removeEventListener('touchmove',   onStickMove);
      stick.removeEventListener('touchend',    onStickEnd);
      stick.removeEventListener('touchcancel', onStickEnd);
      jump.removeEventListener('touchstart',   onJumpStart);
      jump.removeEventListener('touchend',     onJumpEnd);
      jump.removeEventListener('touchcancel',  onJumpEnd);
      action.removeEventListener('touchstart',   onActionStart);
      action.removeEventListener('touchend',     onActionEnd);
      action.removeEventListener('touchcancel',  onActionEnd);
    };
  }, [onStickStart, onStickMove, onStickEnd, onJumpStart, onJumpEnd, onActionStart, onActionEnd]);

  return (
    <div className="mobile-controls" aria-hidden="true">
      <div className="stick-area" ref={stickAreaRef}>
        <div className="stick-ring" />
        <div className="stick-knob" ref={knobRef} />
      </div>

      <div className="mobile-btns">
        <button className="action-btn" ref={actionBtnRef} aria-label="Action">
          E
        </button>
        <button className="jump-btn" ref={jumpBtnRef} aria-label="Jump">
          ↑
        </button>
      </div>
    </div>
  );
};
